import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchHistory } from '@/api/endpoints';
import { formatCount, formatDuration, formatRelativeTime } from '@/lib/format';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/states';
import { asQueryError } from '@/query/execute';

const TIER_WIDTH: Record<string, string> = { small: 'w-1', medium: 'w-2', large: 'w-3' };

export interface QueryHistoryProps {
  readonly onRerun: (sql: string) => void;
}

export function QueryHistory({ onRerun }: QueryHistoryProps): JSX.Element {
  const [search, setSearch] = useState('');
  const history = useQuery({
    queryKey: ['history', search],
    queryFn: () => fetchHistory(search || undefined),
    refetchInterval: 20_000,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="query-history">
      <div className="panel-head">
        <span className="eyebrow">History</span>
        <span className="tnum text-[10px] text-faint">{history.data?.entries.length ?? 0}</span>
      </div>

      <div className="border-b border-line p-1.5">
        <input
          className="field"
          type="search"
          placeholder="Search history"
          aria-label="Search query history"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {history.isLoading ? <LoadingState label="Loading history" /> : null}
        {history.error ? (
          <ErrorState error={asQueryError(history.error)} onRetry={() => void history.refetch()} />
        ) : null}
        {history.data?.entries.length === 0 ? (
          <EmptyState title="No queries yet" hint="Runs from this workspace appear here." />
        ) : null}

        {(history.data?.entries ?? []).map((entry) => (
          <article
            key={entry.queryId}
            className="group border-b border-line px-2.5 py-1.5 transition-colors hover:bg-raised"
          >
            <div className="flex items-center gap-1.5">
              <span
                className={`h-2.5 shrink-0 rounded-sm ${TIER_WIDTH[entry.tier] ?? 'w-1'}`}
                style={{
                  background:
                    entry.status === 'error'
                      ? 'var(--c-danger)'
                      : entry.status === 'cancelled'
                        ? 'var(--c-faint)'
                        : 'var(--c-ok)',
                }}
                title={`${entry.status} · ${entry.tier} duckling`}
              />
              <span className="eyebrow">{entry.tier}</span>
              <span className="tnum text-[10px] text-faint">{formatRelativeTime(entry.startedAt)}</span>
              <button
                type="button"
                className="icon-btn ml-auto h-5 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
                onClick={() => onRerun(entry.sql)}
                aria-label={`Re-run query from ${formatRelativeTime(entry.startedAt)}`}
              >
                ↻ Run
              </button>
            </div>

            <pre className="mt-1 line-clamp-3 whitespace-pre-wrap break-all font-mono text-[11px] leading-4 text-ink">
              {entry.sql}
            </pre>

            <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
              <Metric label="took" value={formatDuration(entry.durationMs)} />
              <Metric label="rows" value={formatCount(entry.rowCount)} />
              <Metric label="duckling" value={entry.ducklingId} />
              {entry.error ? (
                <span className="tnum text-[10px] text-danger" title={entry.error.message}>
                  SQLSTATE {entry.error.sqlState}
                </span>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <span className="flex items-baseline gap-1">
      <span className="eyebrow">{label}</span>
      <span className="tnum text-[10px] text-muted">{value}</span>
    </span>
  );
}
